"use client";
import React, { useEffect, useState } from "react";
import Link from "next/link";

import {
  Stepper,
  StepperDescription,
  StepperIndicator,
  StepperItem,
  StepperSeparator,
  StepperTitle,
  StepperTrigger,
} from "../../../../components/ui/stepper";
import { cn } from "../../../../lib/utils/stylesUtils";
import { usePathname, useRouter } from "next/navigation";
import { useUser } from "../../../../hooks/useUser";
import { useStoreState } from "../../../../hooks/storeHooks";
import { LoadingLabel } from "../../../../components/ui/loading-label";
import { stripeStatus } from "../../../../helpers/stripeStatus";
import { ScrollArea } from "../../../../components/ui/scroll-area";

const steps = [
  {
    step: 1,
    title: "Your details",
    description: "Provide an email and password",
    path: "/register/profile",
    subPaths: [],
  },
  {
    step: 2,
    title: "Your shop",
    description: "Create & customize your shop",
    path: "/register/shop",
    subPaths: ["/register/shop/customize"],
  },
  {
    step: 3,
    title: "Invite your team",
    description: "Start collaborating with your team",
    path: "/register/team",
    subPaths: [],
  },
  {
    step: 4,
    title: "Start accepting payments",
    description: "Accept all card payments online",
    path: "/register/payments",
    subPaths: ["/register/payments/subscribe", "/register/payments/connect"],
  },
];

export default function RegisterLayout({ children }: { children: React.ReactNode }) {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const pathName = usePathname();

  /**
   * Maintain UI of the stepper
   */
  useEffect(() => {
    const step = steps.findIndex((step) => step.path === pathName || step.subPaths.includes(pathName || ""));
    console.log("step found is " + step);
    if (step !== -1) setCurrentStep(step + 1);
  }, [pathName]);

  /**
   * Listen for change in tenant profile
   */
  const { data, mutate, isLoading } = useUser();
  const profile = useStoreState((state) => state.profile);
  const router = useRouter();
  useEffect(() => {
    if (isLoading) return;
    if (!profile) {
      if (pathName != steps[0].path) {
        router.push(steps[0].path);
      }
      //setCurrentStep(1);
    } else if (profile && profile?.profiles_tenants.length == 0) {
      if (pathName != steps[1].path) {
        router.push(steps[1].path);
      }
      //setCurrentStep(2);
    } else if (
      stripeStatus(profile?.profiles_tenants[0].tenants).subscription.onboarded === true ||
      stripeStatus(profile?.profiles_tenants[0].tenants).connect.onboarded === true
    ) {
      console.log("It seems we've either onboarded or connected");
      if (pathName != "/register/payments") {
        router.push("/register/payments");
      }
      //setCurrentStep(4);
    } else if (!profile?.profiles_tenants[0].tenants?.logo_from_storage) {
      if (pathName != "/register/shop/customize") {
        router.push("/register/shop/customize");
      }
      //      setCurrentStep(4);
    } else if (profile && profile?.profiles_tenants.length > 0) {
      /*if (pathName != steps[3].path) {
        router.push(steps[3].path);
      }*/
      if (!["/register/team", "/register/payments/subscribe", "/register/payments/connect"].includes(pathName || "")) {
        router.push("/register/team");
      }
      //    setCurrentStep(4);
    }
  }, [profile, isLoading]);

  return (
    <>
      {isLoading ? (
        <div className="flex min-h-screen items-center justify-center">
          <LoadingLabel />
        </div>
      ) : (
        <div className="flex min-h-screen bg-white">
          {/* Left Sidebar */}
          <div className="w-[400px] p-8 bg-gray-50 m-6 rounded-xl pb-4 hidden sm:block">
            <div className="mb-12 mt-4">
              <img src="/flexchain-logo.png" alt="Flexchain Logo" className="h-10 mx-auto" />
            </div>
            <div>
              <Stepper value={currentStep} onChange={setCurrentStep} orientation="vertical">
                {steps.map(({ step, title, description }) => (
                  <StepperItem key={step} step={step} className={cn("relative items-start not-last:flex-1")}>
                    <StepperTrigger className={cn("items-start rounded pb-12 last:pb-0")} enabled={step < currentStep}>
                      <StepperIndicator />
                      <div className="mt-0.5 space-y-0.5 px-2 text-left ">
                        <StepperTitle className="mb-0">{title}</StepperTitle>
                        <StepperDescription>{description}</StepperDescription>
                      </div>
                    </StepperTrigger>
                    {step < steps.length && (
                      <StepperSeparator className="absolute inset-y-0 top-[calc(1.5rem+0.125rem)] left-3 -order-1 m-0 -translate-x-1/2 group-data-[orientation=horizontal]/stepper:w-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=horizontal]/stepper:flex-none group-data-[orientation=vertical]/stepper:h-[calc(100%-1.5rem-0.25rem)]" />
                    )}
                  </StepperItem>
                ))}
              </Stepper>
            </div>

            <div className="absolute bottom-12 flex w-[calc(400px-4rem)] justify-between">
              <Link href="/" className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2">
                ← Back to home
              </Link>
              {profile === null && (
                <Link href="/login" className="text-sm text-gray-600 hover:text-gray-900">
                  Sign in
                </Link>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="flex flex-col items-center justify-center h-screen centered-layout pr-6 pl-6 sm:pl-0">
              <div
                className={cn(
                  "max-w-md w-full min-w-md relative flex flex-col items-center justify-center min-h-[calc(100vh-200px)]",
                  pathName == "/register/payments/subscribe" && "max-w-5xl"
                )}
              >
                <img src="/favicon.png" className="h-12 w-12 mb-4" />
                <ScrollArea className="max-h-[calc(100vh-200px)] w-full  px-1">
                  <div className="py-2 w-full">{children}</div>
                </ScrollArea>
              </div>
              <p className="text-sm text-muted-foreground mt-4">Need help? Call 855-95-CHAIN for free!</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
